// src/components/ui/features/ClientFeatureTabs.tsx

'use client'

import { memo, useState } from 'react'
import * as Tabs from "@radix-ui/react-tabs"
import { InstaxImageNoCap } from "~/components/ui/InstaxImage"
import Arrow from "~/components/Arrow"
import { featureTabs } from '~/components/ui/features/ClientFeatureTabsData'

// Combine the classes consistently
const getTabClasses = (): string => {
  return `group relative flex flex-1 flex-col items-start justify-start rounded-xl p-6 text-left shadow-lg ring-1 ring-gray-200 dark:ring-white/5 dark:data-[state=active]:shadow-indigo-900/30`
}

export const ClientFeatureTabs = memo(function ClientFeatureTabs() {
  const initialTab = featureTabs?.[0]?.id ?? ''
  const [activeTab, setActiveTab] = useState(initialTab)

  if (!featureTabs.length) return null

  return (
    <Tabs.Root
      value={activeTab}
      onValueChange={setActiveTab}
      orientation="vertical"
      className="mt-14 grid grid-cols-12 gap-8 px-4 md:px-8 justify-center">
      <Tabs.List 
        className="col-span-full flex w-full flex-col gap-6 md:order-2 md:col-span-5"
        aria-label="Feature categories"
        role="tablist"
        aria-orientation="vertical">
        {featureTabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          
          return (
            <Tabs.Trigger
              key={tab.id}
              id={`tab-${tab.id}`}
              value={tab.id}
              className={getTabClasses()}
              role="tab"
              aria-controls={`tabpanel-${tab.id}`}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}>
              <div className="absolute -left-[36px] top-1/2 hidden -rotate-90 sm:group-data-[state=active]:flex">
                <Arrow 
                  width={18} 
                  height={8} 
                  className="fill-gray-950 dark:fill-gray-900" 
                  aria-hidden="true" 
                />
              </div>
              <div className="flex items-center gap-4">
                <span 
                  className="aspect-square w-fit rounded-lg bg-white p-2 text-gray-900 ring-1 ring-black/10 transition-all group-data-[state=active]:text-indigo-600 group-data-[state=active]:shadow-md group-data-[state=active]:shadow-indigo-500/20 dark:bg-gray-950 dark:text-gray-400 dark:ring-white/10 dark:group-data-[state=active]:text-indigo-400 dark:group-data-[state=active]:shadow-indigo-600/50"
                  aria-hidden="true">
                  <Icon className="size-5" />
                </span>
                <h3 className="font-semibold tracking-tight text-gray-900 transition-all group-data-[state=active]:text-indigo-600 sm:text-lg dark:text-gray-400 dark:group-data-[state=active]:text-indigo-400">
                  {tab.title}
                </h3>
              </div>
              <p className="mt-4 leading-7 text-gray-900 dark:text-gray-400">
                {tab.description}
              </p>
            </Tabs.Trigger>
          )
        })}
      </Tabs.List>

      <div className="col-span-full content-center md:col-span-7">
        {featureTabs.map((tab) => {
          const isFirstTab = featureTabs[0] ? tab.id === featureTabs[0].id : false
          
          return (
            <Tabs.Content 
              key={tab.id}
              value={tab.id}
              id={`tabpanel-${tab.id}`}
              role="tabpanel"
              aria-labelledby={`tab-${tab.id}`}
              tabIndex={0}
              className="focus-visible:outline-none">
              <InstaxImageNoCap
                src={tab.imageSrc}
                alt={tab.imageAlt}
                width={480}
                height={480}
                className="max-w-full h-full mx-auto"
                loading={isFirstTab ? 'eager' : 'lazy'}
                placeholder="blur"
                blurDataURL={tab.blurDataUrl}
                quality={70}
                sizes="(max-width: 640px) 100vw, 480px, (max-width: 768px) 50vw, 480px"
              />
            </Tabs.Content>
          )
        })}
      </div>
    </Tabs.Root>
  )
})